import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { Check } from 'lucide-react';
import { clsx } from '../../ds/clsx';
import { Banner, Button, Caret, Segmented, Select, Skeleton } from '../../ds/components';
import { ApiError, errorMessage } from '../../lib/api';
import {
  useQueuePreview,
  useQueueSuggestions,
  useStartQueue,
  type QueueBlock,
  type QueueDialMode,
  type QueueFilter,
  type QueueOrder,
} from './api';
import { QueueFilterPopover, describeFilter, type FilterNames } from './QueueFilterPopover';
import styles from './QueueStartPage.module.css';

/** Blocks preselected on arrival: everything that has entries except the documents (Queue 01). */
const DEFAULT_BLOCKS: readonly QueueBlock[] = ['overdue', 'fresh_leads', 'appointments_without_outcome'];

/**
 * `/tasks/queue` — Queue 01/02: the four building blocks with counts and hints, the own filter with its
 * live preview count, order and dial mode, „Queue starten" (disabled at 0 entries, Queue 02).
 */
export function QueueStartPage() {
  const navigate = useNavigate();
  const suggestions = useQueueSuggestions();
  const start = useStartQueue();
  const [blocks, setBlocks] = useState<QueueBlock[] | null>(null);
  const [filter, setFilter] = useState<QueueFilter | null>(null);
  const [names, setNames] = useState<FilterNames>({});
  const [order, setOrder] = useState<QueueOrder>('score');
  const [dialMode, setDialMode] = useState<QueueDialMode>('preview');
  const [filterOpen, setFilterOpen] = useState(false);
  const filterRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (blocks !== null || !suggestions.data) return;
    setBlocks(
      suggestions.data.blocks
        .filter((block) => block.count > 0 && DEFAULT_BLOCKS.includes(block.key))
        .map((block) => block.key),
    );
  }, [blocks, suggestions.data]);

  const selected = useMemo(() => blocks ?? [], [blocks]);
  const previewBody = useMemo(() => ({ blocks: selected, filter }), [selected, filter]);
  const preview = useQueuePreview(previewBody, selected.length > 0 || filter !== null);
  const count = selected.length > 0 || filter !== null ? (preview.data?.count ?? null) : 0;
  const minutes =
    count === null ? null : (preview.data?.minutes ?? count * (suggestions.data?.minutesPerEntry ?? 5));
  const filterHint = filter !== null && preview.data?.hint ? preview.data.hint : null;
  const canStart = count !== null && count > 0 && !start.isPending;

  function toggleBlock(key: QueueBlock) {
    setBlocks((current) => {
      const list = current ?? [];
      return list.includes(key) ? list.filter((entry) => entry !== key) : [...list, key];
    });
  }

  function startQueue() {
    start.mutate(
      { blocks: selected, filter, order, dialMode },
      { onSuccess: () => void navigate('/tasks/queue/session') },
    );
  }

  const conflictSessionId =
    start.error instanceof ApiError && start.error.status === 409
      ? start.error.detailString('sessionId')
      : null;
  const data = suggestions.data;
  const orderHint = data?.orders.find((entry) => entry.key === order)?.hint ?? null;
  const dialHint = data?.dialModes.find((entry) => entry.key === dialMode)?.hint ?? null;

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.headerText}>
          <div className={styles.crumb}>Tasks</div>
          <h1 className={styles.title}>Queue starten</h1>
          <div className={styles.subtitle}>
            {data
              ? `${data.open.total === 1 ? '1 offene Task' : `${data.open.total} offene Tasks`}${data.open.overdue > 0 ? ` · ${data.open.overdue} überfällig` : ''}`
              : '\u00a0'}
          </div>
        </div>
        <div className={styles.headerEnd}>
          <Link to="/tasks" className={styles.backLink}>
            Zurück zu Meine Tasks
          </Link>
        </div>
      </div>

      {suggestions.isError ? <Banner tone="danger">{errorMessage(suggestions.error)}</Banner> : null}
      {data?.activeSessionId ? (
        <Banner
          tone="accent"
          actions={
            <Button size="small" onClick={() => void navigate('/tasks/queue/session')}>
              Zur Queue
            </Button>
          }
        >
          Sie haben bereits eine laufende Queue.
        </Banner>
      ) : null}

      <div className={styles.card}>
        <div className={styles.cardTitle}>Queue starten</div>
        <div className={styles.cardText}>
          Wählen Sie, woraus die Queue gebaut wird. Die Reihenfolge folgt dem Score und bleibt während der
          Session gleich.
        </div>

        <div className={styles.tiles} role="group" aria-label="Bausteine">
          {data
            ? data.blocks.map((block) => {
                const active = selected.includes(block.key);
                return (
                  <button
                    key={block.key}
                    type="button"
                    role="checkbox"
                    aria-checked={active}
                    className={clsx(styles.tile, active && styles.tileActive)}
                    onClick={() => toggleBlock(block.key)}
                  >
                    <span className={styles.tileHead}>
                      <span
                        className={clsx(styles.tileBox, active && styles.tileBoxActive)}
                        aria-hidden="true"
                      >
                        {active ? <Check size={11} strokeWidth={2.5} /> : null}
                      </span>
                      <span className={clsx(styles.tileLabel, active && styles.tileLabelActive)}>
                        {block.label}
                      </span>
                    </span>
                    <span className={styles.tileCount}>{block.count}</span>
                    <span className={styles.tileHint}>{block.hint ?? ' '}</span>
                  </button>
                );
              })
            : Array.from({ length: 4 }, (_, index) => (
                <div key={index} className={styles.tile} aria-busy="true">
                  <Skeleton width="60%" />
                  <Skeleton width={28} height={22} />
                  <Skeleton width="70%" height={8} />
                </div>
              ))}
        </div>

        <div className={styles.controls}>
          <div className={styles.control}>
            <span className={styles.controlLabel}>Eigener Filter</span>
            <button
              ref={filterRef}
              type="button"
              className={clsx(styles.filterField, filter === null && styles.filterFieldEmpty)}
              aria-haspopup="dialog"
              aria-expanded={filterOpen}
              onClick={() => setFilterOpen((value) => !value)}
            >
              <span className={styles.filterValue}>{describeFilter(filter, names)}</span>
              <Caret size={11} />
            </button>
            <QueueFilterPopover
              open={filterOpen}
              anchorRef={filterRef}
              value={filter}
              names={names}
              onClose={() => setFilterOpen(false)}
              onApply={(next, nextNames) => {
                setFilter(next);
                setNames(nextNames);
              }}
            />
          </div>
          <div className={styles.control}>
            <Select
              label="Reihenfolge"
              value={order}
              options={(
                data?.orders ?? [{ key: 'score', label: 'Score (überfällig zuerst)', hint: null }]
              ).map((entry) => ({
                value: entry.key,
                label: entry.label,
              }))}
              onChange={(event) => setOrder(event.target.value as QueueOrder)}
            />
            {orderHint ? <span className={styles.controlHint}>{orderHint}</span> : null}
          </div>
          <div className={styles.control}>
            <span className={styles.controlLabel}>Anwahlmodus für Anrufe</span>
            <Segmented
              label="Anwahlmodus für Anrufe"
              value={dialMode}
              onChange={setDialMode}
              items={(
                data?.dialModes ?? [
                  { key: 'preview', label: 'Preview', hint: null },
                  { key: 'power', label: 'Power', hint: null },
                ]
              ).map((entry) => ({ value: entry.key, label: entry.label }))}
            />
            {dialHint ? <span className={styles.controlHint}>{dialHint}</span> : null}
          </div>
        </div>

        {filterHint ? (
          <div className={styles.hintBox} role="status">
            <span className={styles.hintTitle}>0 Tasks</span>
            {filterHint}
          </div>
        ) : null}

        {start.isError ? (
          <div className={styles.errorRow}>
            <Banner
              tone="danger"
              actions={
                conflictSessionId ? (
                  <Button
                    size="small"
                    variant="secondary"
                    onClick={() => void navigate('/tasks/queue/session')}
                  >
                    Zur Queue
                  </Button>
                ) : undefined
              }
            >
              {errorMessage(start.error)}
            </Banner>
          </div>
        ) : null}

        <div className={styles.footer}>
          <span className={styles.footerCount}>
            {count === null ? (
              <Skeleton width={120} />
            ) : count === 0 ? (
              '0 Tasks'
            ) : (
              <>
                <strong className={styles.footerStrong}>{count === 1 ? '1 Task' : `${count} Tasks`}</strong> ·
                ca. {minutes === 1 ? '1 Minute' : `${minutes ?? 0} Minuten`}
              </>
            )}
          </span>
          <span className={styles.footerActions}>
            {filter !== null ? (
              <Button
                variant="secondary"
                onClick={() => {
                  setFilter(null);
                  setNames({});
                }}
              >
                Filter entfernen
              </Button>
            ) : null}
            <Button onClick={startQueue} disabled={!canStart} loading={start.isPending}>
              Queue starten
            </Button>
          </span>
        </div>
      </div>
    </div>
  );
}
